import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlaygroundPresentation } from "./playground-presentation";

/** Non-modal desktop panel. Parameter values stay owned by the composer form. */
export function PlaygroundRunSettingsPanel() {
  const { t, i18n } = useTranslation();
  const { settingsOpen, settingsId, settingsOpener, setSettingsHost, closeSettings } = usePlaygroundPresentation();
  const closer = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (settingsOpen && document.activeElement === settingsOpener.current) closer.current?.focus({ preventScroll: true });
  }, [settingsOpen, settingsOpener]);

  const dismiss = () => {
    closeSettings(true);
  };

  if (!settingsOpen) return null;

  return <aside id={settingsId} aria-labelledby={`${settingsId}-title`} hidden={!settingsOpen}
    dir={i18n.dir()} data-playground-settings="desktop"
    className={settingsOpen ? "flex min-h-0 w-[300px] shrink-0 flex-col border-s border-border bg-background text-foreground" : "hidden"}
    onKeyDown={event => {
      if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); dismiss(); }
    }}>
    <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-5">
      <h2 id={`${settingsId}-title`} className="text-sm font-medium leading-5">{t("playground.runSettings")}</h2>
      <Button ref={closer} type="button" variant="ghost" size="icon" aria-label={t("common.close")}
        className="size-6 rounded-sm motion-reduce:transition-none" onClick={dismiss}>
        <X aria-hidden="true" className="size-[13.25px]" />
      </Button>
    </div>
    <div ref={setSettingsHost} className="min-h-0 flex-1 overflow-y-auto overscroll-contain" />
  </aside>;
}
